'use client';

import type {
  CreateListInput,
  Item,
  List,
  ListDetail,
  CreateItemInput as SwishCreateItemInput,
  UpdateListInput,
} from '@swishapp/api-client';
import { WISHLIST_API_PATHS } from './wishlist-constants';
import type { WishlistApiResponse, WishlistError } from './wishlist-types';

// Extended CreateItemInput to optionally include listId
export interface CreateItemInput extends SwishCreateItemInput {
  listId?: string;
}

export class WishlistProxyClient {
  private baseUrl: string;
  private authToken: string;

  constructor(authToken: string, baseUrl = WISHLIST_API_PATHS.BASE) {
    this.authToken = authToken;
    this.baseUrl = baseUrl;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<WishlistApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.authToken}`,
          ...options.headers,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        // Detect authentication failures specifically
        if (response.status === 401 || response.status === 403) {
          const error: WishlistError = {
            code: 'AUTH_ERROR',
            message:
              result.error?.message ||
              `Authentication failed with status ${response.status}`,
            details: {
              status: response.status,
              originalError: result.error,
            },
          };
          return { error };
        }

        // Check if the error message indicates token expiration
        const errorMessage = result.error?.message || '';
        const isTokenExpired =
          errorMessage.toLowerCase().includes('expired') ||
          errorMessage.toLowerCase().includes('invalid token') ||
          errorMessage.toLowerCase().includes('unauthorized');

        const error: WishlistError = result.error || {
          code: isTokenExpired ? 'AUTH_ERROR' : 'REQUEST_FAILED',
          message: `Request failed with status ${response.status}`,
          details: { status: response.status },
        };

        // Note: Error logging is handled by the calling hooks using useLogger

        return { error };
      }

      return { data: result.data };
    } catch (error) {
      const wishlistError: WishlistError = {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error',
        details: { originalError: error },
      };

      // Note: Error logging is handled by the calling hooks using useLogger

      return { error: wishlistError };
    }
  }

  getLists(): Promise<WishlistApiResponse<List[]>> {
    return this.makeRequest<List[]>(WISHLIST_API_PATHS.LISTS);
  }

  getList(id: string): Promise<WishlistApiResponse<ListDetail>> {
    return this.makeRequest<ListDetail>(`${WISHLIST_API_PATHS.LISTS}/${id}`);
  }

  createList(input: CreateListInput): Promise<WishlistApiResponse<ListDetail>> {
    return this.makeRequest<ListDetail>(WISHLIST_API_PATHS.LISTS, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  updateList(
    id: string,
    input: UpdateListInput
  ): Promise<WishlistApiResponse<ListDetail>> {
    return this.makeRequest<ListDetail>(`${WISHLIST_API_PATHS.LISTS}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  deleteList(id: string): Promise<WishlistApiResponse<void>> {
    return this.makeRequest<void>(`${WISHLIST_API_PATHS.LISTS}/${id}`, {
      method: 'DELETE',
    });
  }

  addItem(input: CreateItemInput): Promise<WishlistApiResponse<Item>> {
    return this.makeRequest<Item>(WISHLIST_API_PATHS.ITEMS, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  removeItem(id: string): Promise<WishlistApiResponse<void>> {
    return this.makeRequest<void>(`${WISHLIST_API_PATHS.ITEMS}/${id}`, {
      method: 'DELETE',
    });
  }

  getItems(): Promise<WishlistApiResponse<Item[]>> {
    return this.makeRequest<Item[]>(WISHLIST_API_PATHS.ITEMS);
  }

  linkItemToBoard(
    itemId: string,
    boardId: string
  ): Promise<WishlistApiResponse<void>> {
    return this.makeRequest<void>(
      `${WISHLIST_API_PATHS.ITEMS}/${itemId}/boards`,
      {
        method: 'PUT',
        body: JSON.stringify({ boardId }),
      }
    );
  }

  removeItemFromBoard(
    itemId: string,
    boardId: string
  ): Promise<WishlistApiResponse<void>> {
    return this.makeRequest<void>(
      `${WISHLIST_API_PATHS.ITEMS}/${itemId}/boards/${boardId}`,
      {
        method: 'DELETE',
      }
    );
  }
}
